import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang, LANGUAGES } from "@/context/LangContext";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe, Megaphone } from "lucide-react";
import { useAdImpression, trackAdClick } from "@/lib/adTracking";

// Read city/category context only when the user is on a `/vendors*` page so
// that ribbon/banner/rail ads outside the listing remain global.
function useAdContext() {
  const loc = useLocation();
  const [params] = useSearchParams();
  if (!loc.pathname.startsWith("/vendors")) return { city: "", category: "" };
  return { city: params.get("city") || "", category: params.get("category") || "" };
}

function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const active = LANGUAGES.find((l) => l.id === lang) || LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-white hover:bg-white/15 h-7 px-2 gap-1.5" data-testid="lang-switcher-trigger">
          <Globe className="h-3.5 w-3.5" /> <span className="text-xs font-medium">{active.flag} {active.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.id} onClick={() => setLang(l.id)} data-testid={`lang-${l.id}`} className="cursor-pointer gap-2">
            <span>{l.flag}</span><span className="flex-1">{l.label}</span>
            {lang === l.id && <span className="text-[10px] text-muted-foreground">Active</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Small inner component so the impression hook can run conditionally on the
// `ad_id` of the currently displayed ribbon message.
function RibbonContent({ ad, fallbackTitle }) {
  useAdImpression(ad?.ad_id);
  const title = ad?.title || fallbackTitle;
  const inner = (
    <div className="flex-1 flex items-center gap-2 overflow-hidden">
      <Megaphone className="h-3.5 w-3.5 shrink-0" />
      <div className="truncate font-medium">{title}</div>
    </div>
  );
  if (ad?.url) {
    return (
      <Link to={ad.url} onClick={() => trackAdClick(ad.ad_id)} className="flex-1 min-w-0">
        {inner}
      </Link>
    );
  }
  return inner;
}

export default function TopRibbon() {
  const { t } = useLang();
  const [ribbon, setRibbon] = useState([]);
  const cart = useCart();
  const { city, category } = useAdContext();
  useEffect(() => {
    const params = { slot: "ribbon" };
    if (city) params.city = city;
    if (category) params.category = category;
    api.get("/ads", { params }).then((r) => setRibbon(r.data || [])).catch(() => {});
  }, [city, category]);
  const ad = ribbon[0];
  return (
    <div className="bg-red-700 text-white text-xs sm:text-sm border-b border-red-900" data-testid="top-ribbon">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-9 flex items-center gap-4">
        <RibbonContent ad={ad} fallbackTitle={t("ribbon.welcome")} />
        {cart && (
          <Link to="/cart" data-testid="ribbon-cart-link" className="hidden sm:inline-flex items-center gap-1.5 text-white/95 hover:text-white">
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/15 text-[10px]">{cart.count}</span>
            <span>{t("ribbon.cart")}</span>
          </Link>
        )}
        <LanguageSwitcher />
      </div>
    </div>
  );
}

export function AdBanner() {
  const [ad, setAd] = useState(null);
  const { city, category } = useAdContext();
  useEffect(() => {
    const params = { slot: "banner" };
    if (city) params.city = city;
    if (category) params.category = category;
    api.get("/ads", { params }).then((r) => setAd((r.data || [])[0])).catch(() => {});
  }, [city, category]);
  useAdImpression(ad?.ad_id);
  if (!ad) return null;
  return (
    <Link
      to={ad.url || "#"}
      onClick={() => trackAdClick(ad.ad_id)}
      data-testid="ad-banner"
      className="block"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2">
        <div className="rounded-xl px-5 py-3 text-sm text-white flex items-center gap-3 shadow-sm"
             style={{ background: `linear-gradient(90deg, ${ad.color || "#f97316"}, ${ad.color || "#ea7a1c"}cc)` }}>
          <Megaphone className="h-4 w-4 shrink-0" />
          <div className="flex-1">
            <strong>{ad.title}</strong>
            {ad.subtitle && <span className="opacity-90"> · {ad.subtitle}</span>}
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] opacity-80">Sponsored</span>
        </div>
      </div>
    </Link>
  );
}

// Single rail card with its own impression hook (so each ad gets tracked).
function AdRailItem({ ad }) {
  useAdImpression(ad.ad_id);
  return (
    <Link
      key={ad.ad_id}
      to={ad.url || "#"}
      onClick={() => trackAdClick(ad.ad_id)}
      className="rounded-2xl border border-border/60 overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all bg-card"
    >
      {ad.image_url ? (
        <img src={ad.image_url} alt="" className="w-full aspect-[4/3] object-cover" />
      ) : (
        <div className="h-10 w-10 rounded-xl flex items-center justify-center m-3"
             style={{ background: `${ad.color || "#f97316"}22`, color: ad.color || "#f97316" }}>
          <Icon name={ad.icon || "Tag"} className="h-5 w-5" />
        </div>
      )}
      <div className="p-3 pt-2">
        <div className="font-semibold text-sm leading-tight">{ad.title}</div>
        {ad.subtitle && <div className="text-xs text-muted-foreground mt-1">{ad.subtitle}</div>}
      </div>
    </Link>
  );
}

export function AdRail({ side = "left" }) {
  const [ads, setAds] = useState([]);
  const { city, category } = useAdContext();
  useEffect(() => {
    const params = { slot: `rail-${side}` };
    if (city) params.city = city;
    if (category) params.category = category;
    api.get("/ads", { params }).then((r) => setAds(r.data || [])).catch(() => {});
  }, [side, city, category]);
  if (!ads.length) return null;
  return (
    <aside className="hidden xl:flex flex-col gap-3 sticky top-24 w-[180px] shrink-0" data-testid={`ad-rail-${side}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Sponsored</div>
      {ads.map((a) => (
        <AdRailItem key={a.ad_id} ad={a} />
      ))}
    </aside>
  );
}
